import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { Card, CardBody } from '@heroui/react';
import React from 'react';
import { OrderBoardCard as OrderBoardCardType } from '../../types/orderBoard';

interface BoardCardProps {
  card: OrderBoardCardType;
  onClick?: () => void;
  isLocked?: boolean;
}

const BoardCard: React.FC<BoardCardProps> = ({
  card,
  onClick,
  isLocked = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id.toString(),
    data: {
      type: 'card',
      card,
    },
    disabled: isLocked,
  });

  // Only apply transform and transition when actively dragging
  // When drag ends, the card should appear in its new position without animating back
  const style = {
    transform: isDragging ? CSS.Transform.toString(transform) : 'none',
    transition: isDragging ? transition : 'none',
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    // Prevent onClick when dragging
    if (isDragging) {
      return;
    }

    // Stop propagation to prevent drag from starting
    e.stopPropagation();
    e.preventDefault();

    // Trigger onClick handler
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isLocked ? {} : listeners)}
      className='touch-none'
    >
      <Card
        className={`${
          isLocked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
        } hover:shadow-md transition-all select-none ${
          isDragging
            ? 'ring-2 ring-primary-500 opacity-50'
            : isLocked
            ? ''
            : 'hover:scale-105 active:scale-95'
        }`}
      >
        <CardBody className='p-2 md:p-3'>
          <div className='flex items-start gap-2'>
            <div className='text-gray-400 mt-1 flex-shrink-0'>
              <Bars3Icon className='h-4 w-4 md:h-5 md:w-5 rotate-90' />
            </div>
            <div className='flex-1 min-w-0'>
              <h4
                onClick={handleTitleClick}
                className={`font-semibold text-xs md:text-sm text-gray-900 truncate ${
                  isLocked
                    ? ''
                    : 'underline cursor-pointer hover:text-primary-600 transition-colors'
                }`}
              >
                {card.title}
              </h4>
              {card.description && (
                <p className='text-xs text-gray-500 mt-1 line-clamp-2'>
                  {card.description}
                </p>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default BoardCard;
